import React, { useState } from "react";
import { Link } from "react-router-dom";
import arrowBackIcon from "../../img/arrow_back_ios-black-36dp.svg";
import Notification from "../../Components/Notification/Notification";

const ForgotPassword = ({ baseUrl }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
  };

  const validate = () => {
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!email || !emailValid) {
      setEmailError(true);
      setEmailErrorMessage(!email ? "Email is required." : "Invalid email.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError("");
    setEmailError(false);
    setEmailErrorMessage("");

    if (!validate()) return;

    setIsLoading(true);
    try {
      const url = `${baseUrl}/api/v1/auth/forgot-password`;
      const response = await fetch(url, {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (response.ok) {
        setSent(true);
      } else {
        const data = await response.json().catch(() => null);
        setError(data?.message || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center">
          <div className="flex w-full flex-1 items-center">
            <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm text-center">
              <div className="mb-2 flex justify-center">
                <svg className="h-12 w-12 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="mb-3 text-2xl font-semibold text-slate-900">
                Check Your Email
              </div>
              <p className="mb-6 text-sm text-slate-600">
                If an account exists for <span className="font-medium">{email}</span>,
                you'll receive a password reset link shortly.
              </p>
              <Link
                to="/login"
                className="inline-block w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const submitButton = (
    <span className="flex items-center justify-center gap-2">
      {isLoading && (
        <svg
          className="h-4 w-4 animate-spin text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      <span>Send Reset Link</span>
    </span>
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center">
        <div className="mb-4 flex w-full justify-start">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <img src={arrowBackIcon} alt="" className="h-5 w-5" />
            Back to Login
          </Link>
        </div>
        <div className="flex w-full flex-1 items-center">
          <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-2 text-center text-2xl font-semibold text-slate-900">
              Forgot Password
            </div>
            <p className="mb-6 text-center text-sm text-slate-600">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <div className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-slate-500 ${
                    emailError ? "border-red-500" : "border-slate-300"
                  }`}
                  autoFocus
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value);
                    setEmailError(e.target.value !== "" && !valid);
                    setEmailErrorMessage(
                      e.target.value !== "" && !valid ? "Invalid email." : ""
                    );
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSubmit();
                  }}
                />
                {emailErrorMessage && (
                  <div className="mt-1 text-sm text-red-600">{emailErrorMessage}</div>
                )}
              </div>

              {error && <div className="text-sm text-red-600">{error}</div>}

              <button
                type="button"
                disabled={isLoading}
                className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                onClick={handleSubmit}
              >
                {submitButton}
              </button>

              <div className="text-center text-sm text-slate-600">
                <span>Remember your password?</span>{" "}
                <Link to="/login" className="font-semibold text-slate-900">
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Notification
        message={notification.message}
        type={notification.type}
        show={notification.show}
        onClose={() => setNotification((n) => ({ ...n, show: false }))}
      />
    </div>
  );
};

export default ForgotPassword;
